import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Download, ExternalLink, Film, Loader2, Mic2, PencilLine, Play, ScrollText, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { ScriptAudioAlignPipelineDialog } from '@/components/scriptAudioAlign/ScriptAudioAlignPipelineDialog';
import { ScriptAudioAlignHistoryPanel } from '@/components/scriptAudioAlign/ScriptAudioAlignHistoryPanel';
import { VisualPlanEditor } from '@/components/scriptAudioAlign/VisualPlanEditor';
import { ScriptAudioAlignPreview } from '@/components/scriptAudioAlign/ScriptAudioAlignPreview';
import { CoursePlannerPanel } from '@/components/scriptAudioAlign/CoursePlannerPanel';
import { CourseExplainPanel } from '@/components/scriptAudioAlign/CourseExplainPanel';
import { flowHistoryPanelPaddingClass } from '@/components/creationFlows/FlowHistoryPanel';
import {
  COURSE_DEPTHS,
  courseLanguageMeta,
  planCourse,
  startCourseExplain,
  joinExplainNarrationScript,
  applyCourseExplainVisualPlan,
  openScriptAudioAlignInEditor,
  exportScriptAudioAlignVideo,
  downloadScriptAudioAlignMp4,
} from '@/lib/scriptAudioAlignCourse';
import {
  hydrateFormFromJobRequest,
  readScriptAudioAlignHistoryOpen,
  syncScriptAudioAlignSearchParams,
  writeScriptAudioAlignHistoryOpen,
} from '@/lib/scriptAudioAlignHistory';
import { postScriptAudioAlignProcess } from '@/lib/scriptAudioAlign/processRunner';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { languageToMurfLocale, murfVoicesForLanguage, bestMurfLocaleForVoice } from '@/lib/murfVoices';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const DEFAULT_VOICES = {
  openai: 'alloy',
  edge: 'en-US-AriaNeural',
  murf: 'en-US-natalie',
};

const VOICE_HINTS = {
  openai: 'alloy, nova, shimmer…',
  edge: 'en-US-AriaNeural, hi-IN-SwaraNeural…',
  murf: 'en-US-natalie (Murf voice id)',
};

export function ScriptAudioAlignPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStoryId = searchParams.get('storyId');
  const urlJobId = searchParams.get('jobId');

  const [mode, setMode] = useState('script');
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [courseNotes, setCourseNotes] = useState('');
  const [courseDepth, setCourseDepth] = useState('standard');
  const [coursePlan, setCoursePlan] = useState(null);
  const [coursePlanning, setCoursePlanning] = useState(false);
  const [courseExplainJobId, setCourseExplainJobId] = useState(null);
  const [courseExplainLoading, setCourseExplainLoading] = useState(false);
  const [coursePreviewLoading, setCoursePreviewLoading] = useState(false);
  const [courseExplainJob, setCourseExplainJob] = useState(null);
  const [courseNarrationLanguage, setCourseNarrationLanguage] = useState('en');
  const [courseNarrationTone, setCourseNarrationTone] = useState('serious');
  const [openingEditor, setOpeningEditor] = useState(false);
  const [exportingVideo, setExportingVideo] = useState(false);
  const [exportedVideoUrl, setExportedVideoUrl] = useState(null);
  const [ttsProvider, setTtsProvider] = useState('openai');
  const [voice, setVoice] = useState(DEFAULT_VOICES.openai);
  const [language, setLanguage] = useState('en');
  const [murfVoices, setMurfVoices] = useState([]);
  const [murfVoicesLoading, setMurfVoicesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingJobId, setLoadingJobId] = useState(null);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [storyId, setStoryId] = useState(urlStoryId);
  const [jobId, setJobId] = useState(urlJobId);
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(() => readScriptAudioAlignHistoryOpen(true));
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const urlLoadedRef = useRef(false);
  const intakePrefillAppliedRef = useRef(false);
  const agentHandoff = useMemo(
    () => (urlJobId || urlStoryId ? null : readAgentHandoff(location.state)),
    // Only the state that opened the page decides; the URL gains ids once a run starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.state],
  );
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    if (intakePrefillAppliedRef.current) return;
    if (urlJobId || urlStoryId) return;
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    intakePrefillAppliedRef.current = true;
    if (nav.title) setTitle(nav.title);
    if (nav.language) {
      setLanguage(nav.language);
      setCourseNarrationLanguage(nav.language);
    }
    const preferTopic = nav.mode === 'topic' || (!nav.fullScript && nav.tutorialTopic);
    if (preferTopic) {
      setMode('topic');
      setCourseNotes(nav.tutorialTopic || nav.text || '');
    } else if (nav.fullScript || nav.text) {
      setMode('script');
      setScript(nav.fullScript || nav.text);
    }
    if (nav.quick?.storyId) setStoryId(nav.quick.storyId);
    if (readAgentHandoff(location.state)) setHandoffReady(true);
  }, [location.state, urlJobId, urlStoryId]);

  const syncUrl = useCallback((nextStoryId, nextJobId) => {
    setSearchParams(
      (prev) => syncScriptAudioAlignSearchParams(prev, {
        storyId: nextStoryId || null,
        jobId: nextJobId || null,
      }),
      { replace: true },
    );
  }, [setSearchParams]);

  const applyJobPayload = useCallback((detail) => {
    const req = detail?.request || {};
    const data = detail?.result || detail;
    const hydrated = hydrateFormFromJobRequest(req);
    setTitle(hydrated.title || detail?.title || '');
    setScript(hydrated.script);
    setTtsProvider(hydrated.ttsProvider);
    if (hydrated.voice) setVoice(hydrated.voice);
    setLanguage(hydrated.language || 'en');
    setResult(data);
    const sid = detail?.story_id || data?.story_id || req.story_id || null;
    const jid = detail?.job_id || data?.job_id || null;
    setJobId(jid);
    setStoryId(sid);
    syncUrl(sid, jid);
  }, [syncUrl]);

  const loadFromUrl = useCallback(async () => {
    if (urlLoadedRef.current) return;
    if (!urlJobId && !urlStoryId) return;
    urlLoadedRef.current = true;
    setLoadingJobId('url');
    try {
      if (urlJobId) {
        const { data } = await api.get(`/script-audio-align/jobs/${urlJobId}`);
        applyJobPayload(data);
        return;
      }
      if (urlStoryId) {
        try {
          const { data } = await api.get(`/script-audio-align/jobs/by-story/${urlStoryId}`);
          applyJobPayload({ result: data, story_id: urlStoryId, job_id: data.job_id, request: {} });
        } catch {
          const { data: story } = await api.get(`/stories/${urlStoryId}`);
          setTitle(story.title || '');
          setScript(story.user_full_story || '');
          setStoryId(urlStoryId);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load saved job');
    } finally {
      setLoadingJobId(null);
    }
  }, [applyJobPayload, urlJobId, urlStoryId]);

  useEffect(() => {
    loadFromUrl();
  }, [loadFromUrl]);

  useEffect(() => {
    setStoryId(urlStoryId);
    setJobId(urlJobId);
  }, [urlStoryId, urlJobId]);

  useEffect(() => {
    setVoice((prev) => {
      const defaultForProvider = DEFAULT_VOICES[ttsProvider] || DEFAULT_VOICES.openai;
      const knownDefaults = Object.values(DEFAULT_VOICES);
      if (!prev || knownDefaults.includes(prev)) return defaultForProvider;
      return prev;
    });
  }, [ttsProvider]);

  useEffect(() => {
    if (ttsProvider !== 'murf') return undefined;
    let cancelled = false;
    setMurfVoicesLoading(true);
    fetchMurfVoiceCatalog(api)
      .then(({ data }) => {
        if (cancelled) return;
        setMurfVoices(data?.voices || []);
      })
      .catch(() => {
        if (!cancelled) {
          setMurfVoices([]);
          toast.error('Could not load Murf voices — check MURF_API_KEY');
        }
      })
      .finally(() => {
        if (!cancelled) setMurfVoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ttsProvider]);

  const murfVoiceOptions = useMemo(
    () => murfVoicesForLanguage(murfVoices, language),
    [murfVoices, language],
  );

  const selectedMurfVoice = useMemo(() => {
    if (ttsProvider !== 'murf' || !voice) return null;
    return murfVoiceOptions.find((v) => (v.voice_id || v.id) === voice)
      || murfVoices.find((v) => (v.voice_id || v.id) === voice)
      || null;
  }, [ttsProvider, voice, murfVoiceOptions, murfVoices]);

  const murfEffectiveLocale = useMemo(() => {
    if (ttsProvider !== 'murf') return languageToMurfLocale(language);
    if (selectedMurfVoice) {
      return selectedMurfVoice.effective_locale
        || bestMurfLocaleForVoice(selectedMurfVoice, languageToMurfLocale(language));
    }
    return languageToMurfLocale(language);
  }, [ttsProvider, selectedMurfVoice, language]);

  useEffect(() => {
    if (ttsProvider !== 'murf' || !murfVoiceOptions.length) return;
    const ids = murfVoiceOptions.map((v) => v.voice_id || v.id);
    if (voice && ids.includes(voice)) return;
    const preferredLocale = languageToMurfLocale(language);
    const preferred = murfVoiceOptions.find((v) => (v.effective_locale || v.locale) === preferredLocale)
      || murfVoiceOptions[0];
    const nextId = preferred?.voice_id || preferred?.id;
    if (nextId) setVoice(nextId);
  }, [ttsProvider, murfVoiceOptions, language, voice]);

  const handleHistoryToggle = () => {
    setHistoryOpen((prev) => {
      const next = !prev;
      writeScriptAudioAlignHistoryOpen(next);
      return next;
    });
  };

  const handleNewProject = () => {
    urlLoadedRef.current = false;
    setTitle('');
    setScript('');
    setCourseNotes('');
    setCoursePlan(null);
    setCourseExplainJobId(null);
    setCourseExplainJob(null);
    setResult(null);
    setStoryId(null);
    setJobId(null);
    setError(null);
    syncUrl(null, null);
    toast.message('New alignment');
  };

  const runCoursePlan = async () => {
    if (!title.trim()) {
      toast.error('Enter a topic title first');
      return;
    }
    setCoursePlanning(true);
    setCoursePlan(null);
    setCourseExplainJobId(null);
    setCourseExplainJob(null);
    try {
      const data = await planCourse({
        title,
        notes: courseNotes,
        depth: courseDepth,
        storyId: storyId || urlStoryId || null,
      });
      setCoursePlan(data);
      toast.success(data?.used_llm ? 'Course planned' : 'Course planned (heuristic)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not plan course');
    } finally {
      setCoursePlanning(false);
    }
  };

  const handleUseGeneratedScript = (generated) => {
    setScript(generated);
    setMode('script');
    toast.success('Script loaded — review, then run alignment');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStartCourseExplain = async (plan, options = {}) => {
    if (!plan?.curriculum?.topics?.length) {
      toast.error('Plan a curriculum first');
      return;
    }
    const language = options.language || courseNarrationLanguage || 'en';
    const narrationTone = options.tone || courseNarrationTone || 'serious';
    setCourseNarrationLanguage(language);
    setCourseNarrationTone(narrationTone);
    setCourseExplainLoading(true);
    setCourseExplainJob(null);
    try {
      const data = await startCourseExplain({
        title: plan.title || title,
        difficulty: plan.domain_analysis?.difficulty || 'intermediate',
        curriculum: plan.curriculum,
        blueprint: plan.blueprint,
        artifactSelection: plan.artifact_selection,
        language,
        narrationTone,
      });
      setCourseExplainJobId(data.job_id);
      // Align page TTS defaults with chosen narration language.
      const meta = courseLanguageMeta(language);
      setLanguage(language);
      if (ttsProvider === 'edge') setVoice(meta.edgeVoice);
      else if (ttsProvider === 'murf') setVoice(meta.murfVoice);
      toast.success(`Artifact generation queued (${meta.label}, ${narrationTone === 'genz' ? 'Funny GenZ' : 'Serious'})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start artifact generation');
    } finally {
      setCourseExplainLoading(false);
    }
  };

  const handleCreateRenderPreview = async (explainJob) => {
    const job = explainJob || courseExplainJob;
    const narrationScript = joinExplainNarrationScript(job, title).trim()
      || (job?.generated_script || '').trim()
      || script.trim();
    if (!job?.units?.length) {
      toast.error('No lesson artifacts to preview yet');
      return;
    }
    if (!narrationScript) {
      toast.error('No narration script yet — wait for per-subtopic scripts to finish');
      return;
    }
    if (!title.trim()) {
      toast.error('Enter a topic title first');
      return;
    }
    const explainJobId = job.job_id || courseExplainJobId;
    if (!explainJobId) {
      toast.error('Missing explain job id');
      return;
    }

    setCoursePreviewLoading(true);
    setLoading(true);
    setError(null);
    setPipelineOpen(true);
    try {
      setScript(narrationScript);

      let linkedStoryId = storyId || urlStoryId || result?.story_id || null;
      if (!linkedStoryId) {
        const { data: story } = await api.post('/stories', {
          title: title.trim(),
          user_full_story: narrationScript,
          user_topic: title.trim(),
          video_style: 'narrated',
          story_type: 'original',
          tone: 'informative',
        });
        linkedStoryId = story.id;
        setStoryId(linkedStoryId);
        syncUrl(linkedStoryId, jobId);
      } else {
        await api.patch(`/stories/${linkedStoryId}`, {
          title: title.trim(),
          user_full_story: narrationScript,
          user_topic: title.trim(),
        });
      }

      const aligned = await postScriptAudioAlignProcess({
        script: narrationScript,
        title: title.trim(),
        story_id: linkedStoryId,
        tts_provider: ttsProvider,
        voice: voice.trim() || undefined,
        language: ttsProvider === 'murf'
          ? (murfEffectiveLocale || language.trim() || undefined)
          : (language.trim() || undefined),
      }, {
        onProgress: (partial) => {
          setResult((prev) => ({ ...(prev || {}), ...partial, story_id: linkedStoryId }));
          if (partial?.job_id) {
            setJobId(partial.job_id);
            syncUrl(linkedStoryId, partial.job_id);
          }
        },
      });

      if (aligned?.status !== 'completed') {
        throw new Error(aligned?.error_message || 'Alignment did not complete');
      }
      if (aligned?.recovered) {
        toast.message('Alignment finished on the server after the browser wait window');
      }

      // ChatGPT places topic / subtopic / artifact triggers onto the aligned narration.
      const preview = await applyCourseExplainVisualPlan(aligned.job_id, explainJobId, {
        buildPreview: true,
      });

      const finalStoryId = preview.story_id || aligned.story_id || linkedStoryId;
      const finalJobId = preview.job_id || aligned.job_id;
      setResult({ ...preview, story_id: finalStoryId });
      setStoryId(finalStoryId);
      setJobId(finalJobId);
      setScript(narrationScript);
      syncUrl(finalStoryId, finalJobId);
      setHistoryRefreshKey((k) => k + 1);
      toast.success('Render preview ready');
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          document.getElementById('saa-render-preview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail?.message || err.message || 'Could not create render preview');
      if (err.partialResult) {
        setResult({ ...err.partialResult, story_id: storyId || urlStoryId || err.partialResult.story_id });
        if (err.partialResult.job_id) {
          setJobId(err.partialResult.job_id);
          syncUrl(storyId || urlStoryId, err.partialResult.job_id);
        }
      }
      setError(message);
      toast.error(message);
    } finally {
      setCoursePreviewLoading(false);
      setLoading(false);
    }
  };

  const previewReady = Boolean(
    result?.status === 'completed'
    && result?.job_id
    && result?.screenplay_events?.length
    && result?.visual_elements?.length,
  );

  const handleOpenInEditor = async () => {
    if (!result?.job_id) {
      toast.error('No aligned job to open');
      return;
    }
    if (!previewReady) {
      toast.error('Build render preview first');
      return;
    }
    setOpeningEditor(true);
    try {
      const meta = await openScriptAudioAlignInEditor(result.job_id);
      if (meta.story_id) {
        setStoryId(meta.story_id);
        setResult((prev) => (prev ? { ...prev, story_id: meta.story_id } : prev));
        syncUrl(meta.story_id, result.job_id);
      }
      toast.success(`Opened ${meta.scene_count || 0} slides in the video editor`);
      navigate(meta.editor_path || `/stories/${meta.story_id}/video-editor`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not open editor');
    } finally {
      setOpeningEditor(false);
    }
  };

  const handleExportMp4 = async () => {
    if (!result?.job_id) {
      toast.error('No aligned job to export');
      return;
    }
    if (!previewReady) {
      toast.error('Build render preview first');
      return;
    }
    setExportingVideo(true);
    try {
      const meta = await exportScriptAudioAlignVideo(result.job_id);
      setExportedVideoUrl(meta.video_url);
      if (meta.story_id) {
        setStoryId(meta.story_id);
        syncUrl(meta.story_id, result.job_id);
      }
      await downloadScriptAudioAlignMp4(meta.video_url, meta.filename);
      toast.success('MP4 exported to S3 and downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Export failed');
    } finally {
      setExportingVideo(false);
    }
  };

  const handleSelectHistoryJob = async (job) => {
    urlLoadedRef.current = true;
    setLoadingJobId(job.job_id);
    try {
      const { data } = await api.get(`/script-audio-align/jobs/${job.job_id}`);
      applyJobPayload(data);
      toast.success('Loaded from history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load job');
    } finally {
      setLoadingJobId(null);
    }
  };

  const runPipeline = async () => {
    const trimmed = script.trim();
    if (!trimmed) {
      toast.error('Enter a script first');
      return;
    }
    if (!title.trim()) {
      toast.error('Enter a story title first');
      return;
    }
    setLoading(true);
    setError(null);
    setPipelineOpen(true);
    try {
      let linkedStoryId = storyId || urlStoryId || result?.story_id || null;
      if (!linkedStoryId) {
        const { data: story } = await api.post('/stories', {
          title: title.trim(),
          user_full_story: trimmed,
          user_topic: title.trim(),
          video_style: 'narrated',
          story_type: 'original',
          tone: 'informative',
        });
        linkedStoryId = story.id;
        setStoryId(linkedStoryId);
        syncUrl(linkedStoryId, jobId);
        toast.success('Story created');
      } else {
        await api.patch(`/stories/${linkedStoryId}`, {
          title: title.trim(),
          user_full_story: trimmed,
          user_topic: title.trim(),
        });
      }

      const data = await postScriptAudioAlignProcess({
        script: trimmed,
        title: title.trim(),
        story_id: linkedStoryId,
        tts_provider: ttsProvider,
        voice: voice.trim() || undefined,
        language: ttsProvider === 'murf'
          ? (murfEffectiveLocale || language.trim() || undefined)
          : (language.trim() || undefined),
      }, {
        onProgress: (partial) => {
          setResult((prev) => ({ ...(prev || {}), ...partial, story_id: linkedStoryId }));
          if (partial?.job_id) {
            setJobId(partial.job_id);
            syncUrl(linkedStoryId, partial.job_id);
          }
        },
      });

      const finalStoryId = data.story_id || linkedStoryId;
      const finalJobId = data.job_id;
      setResult({ ...data, story_id: finalStoryId });
      setStoryId(finalStoryId);
      setJobId(finalJobId);
      syncUrl(finalStoryId, finalJobId);
      setHistoryRefreshKey((k) => k + 1);

      if (data?.status === 'completed') {
        toast.success(data?.recovered ? 'Script aligned to audio (recovered from server)' : 'Script aligned to audio');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let message = 'Pipeline failed';
      let partial = null;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        message = detail.message || message;
        if (Array.isArray(detail.pipeline_steps)) {
          partial = {
            status: 'failed',
            job_id: detail.job_id,
            pipeline_steps: detail.pipeline_steps,
            error_message: message,
            story_id: storyId,
          };
          if (detail.job_id) {
            setJobId(detail.job_id);
            syncUrl(storyId, detail.job_id);
          }
        }
      } else if (err.message) {
        message = err.message;
      }
      if (err.partialResult) {
        partial = err.partialResult;
      }
      setError(message);
      if (partial) setResult(partial);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResultUpdate = (data) => {
    setResult(data);
    setHistoryRefreshKey((k) => k + 1);
  };

  const activeStoryId = storyId || urlStoryId;
  const activeJobId = jobId || urlJobId || result?.job_id;

  return (
    <div className={`relative min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] transition-[padding] duration-200 ease-out ${flowHistoryPanelPaddingClass(historyOpen)}`}>
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel={mode === 'topic' ? 'The course planner' : 'Script alignment'}
        busy={mode === 'topic' ? coursePlanning : loading}
        onStart={mode === 'topic' ? runCoursePlan : runPipeline}
        startLabel={mode === 'topic' ? 'Plan course' : 'Start generation'}
        fields={[
          {
            id: 'title',
            label: mode === 'topic' ? 'Topic title' : 'Story title',
            valid: Boolean(title.trim()),
            render: () => <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. How vaccines work" autoFocus />,
          },
          ...(mode === 'topic'
            ? []
            : [
                {
                  id: 'script',
                  label: 'Narration script',
                  valid: Boolean(script.trim()),
                  render: () => (
                    <Textarea value={script} onChange={(e) => setScript(e.target.value)} rows={8} placeholder="Paste the script to narrate and align…" />
                  ),
                },
              ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-violet-500">
            <ScrollText className="w-6 h-6" />
            <h1 className="text-2xl font-semibold">Script → audio alignment</h1>
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            Converts your script to speech, then records sentence and word timestamps.
            Open the pipeline popup to inspect each step&apos;s input and output.
          </p>
          {activeStoryId ? (
            <p className="text-[11px] font-mono text-slate-500 break-all">
              storyId=
              {activeStoryId}
              {activeJobId ? ` · jobId=${activeJobId}` : ''}
            </p>
          ) : null}
        </div>

        {loadingJobId ? (
          <p className="text-sm text-slate-500">Loading saved job…</p>
        ) : null}

        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl border" style={{ borderColor: 'var(--glass-border)' }}>
          <button
            type="button"
            onClick={() => setMode('script')}
            className={cn(
              'flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              mode === 'script' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-400 hover:text-slate-200',
            )}
          >
            <PencilLine className="w-4 h-4" />
            Provide a script
          </button>
          <button
            type="button"
            onClick={() => setMode('topic')}
            className={cn(
              'flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              mode === 'topic' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-400 hover:text-slate-200',
            )}
          >
            <TwinneeLogo className="w-4 h-4" />
            Analyze a topic
          </button>
        </div>

        <div className="space-y-4 rounded-2xl border p-5" style={{ borderColor: 'var(--glass-border)' }}>
          <div className="space-y-2">
            <Label htmlFor="saa-title">{mode === 'topic' ? 'Topic title' : 'Story title'}</Label>
            <Input
              id="saa-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={mode === 'topic' ? 'e.g. Binary search, Photosynthesis, OAuth 2.0…' : 'My narration story'}
            />
          </div>

          {mode === 'script' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="saa-script">Script</Label>
                <Textarea
                  id="saa-script"
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  rows={10}
                  placeholder="Enter sentences. Each sentence becomes a TTS clip with word timings."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>TTS provider</Label>
                  <Select value={ttsProvider} onValueChange={setTtsProvider}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="edge">Edge TTS</SelectItem>
                      <SelectItem value="murf">Murf</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Voice</Label>
                  {ttsProvider === 'murf' ? (
                    <Select
                      value={voice || undefined}
                      onValueChange={setVoice}
                      disabled={murfVoicesLoading && !murfVoiceOptions.length}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={murfVoicesLoading ? 'Loading Murf voices…' : 'Select Murf voice'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {murfVoiceOptions.map((v) => {
                          const id = v.voice_id || v.id;
                          const label = [v.display_name || v.name || id, v.gender, v.effective_locale || v.locale]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <SelectItem key={id} value={id}>{label}</SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={voice}
                      onChange={(e) => setVoice(e.target.value)}
                      placeholder={VOICE_HINTS[ttsProvider]}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en" />
                </div>
              </div>
              {ttsProvider === 'murf' ? (
                <p className="text-[11px] text-slate-500">
                  Uses Murf API (`MURF_API_KEY`). Locale for this voice: {murfEffectiveLocale}
                  {voice ? ` · Voice id: ${voice}` : ''}.
                </p>
              ) : null}

              <Button type="button" onClick={runPipeline} disabled={loading || Boolean(loadingJobId)} className="w-full sm:w-auto">
                {loading ? <Mic2 className="w-4 h-4 mr-2 animate-pulse" /> : <Play className="w-4 h-4 mr-2" />}
                {loading ? 'Running pipeline…' : 'Run alignment pipeline'}
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="saa-notes">Extra context (optional)</Label>
                <Textarea
                  id="saa-notes"
                  value={courseNotes}
                  onChange={(e) => setCourseNotes(e.target.value)}
                  rows={3}
                  placeholder="Audience, angle, constraints… anything to steer the plan."
                />
              </div>

              <div className="space-y-2 sm:max-w-xs">
                <Label>Depth</Label>
                <Select value={courseDepth} onValueChange={setCourseDepth}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COURSE_DEPTHS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <p className="text-xs text-slate-500">
                Analyzes the domain and plans a curriculum with artifacts for each topic/subtopic.
                Then generate lesson artifacts with per-artifact narration (language + Serious / Funny GenZ).
              </p>

              <Button type="button" onClick={runCoursePlan} disabled={coursePlanning} className="w-full sm:w-auto">
                {coursePlanning ? <Wand2 className="w-4 h-4 mr-2 animate-pulse" /> : <Wand2 className="w-4 h-4 mr-2" />}
                {coursePlanning ? 'Planning…' : 'Analyze & plan course'}
              </Button>
            </>
          )}
        </div>

        {mode === 'topic' && (coursePlan || coursePlanning) ? (
          <CoursePlannerPanel
            plan={coursePlan}
            loading={coursePlanning}
            onStartExplain={handleStartCourseExplain}
            explainLoading={courseExplainLoading}
            narrationLanguage={courseNarrationLanguage}
            narrationTone={courseNarrationTone}
            onNarrationLanguageChange={setCourseNarrationLanguage}
            onNarrationToneChange={setCourseNarrationTone}
          />
        ) : null}

        {mode === 'topic' && courseExplainJobId ? (
          <CourseExplainPanel
            jobId={courseExplainJobId}
            onJobUpdate={setCourseExplainJob}
            onCreatePreview={handleCreateRenderPreview}
            onUseScript={handleUseGeneratedScript}
            previewLoading={coursePreviewLoading}
          />
        ) : null}

        {result?.status === 'completed' && result?.job_id ? (
          <>
            {activeStoryId ? (
              <div className="rounded-xl border border-violet-500/30 bg-violet-950/20 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-violet-300">Story ID</p>
                  <p className="text-sm font-mono text-slate-200 break-all">{activeStoryId}</p>
                </div>
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link to={`/stories/${activeStoryId}/edit`}>
                    Open storyboard
                    <ExternalLink className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            ) : null}
            <div id="saa-render-preview" className="space-y-4">
              <VisualPlanEditor
                jobId={result.job_id}
                result={result}
                onResultUpdate={handleResultUpdate}
              />
              {previewReady ? (
                <div className="rounded-xl border border-emerald-500/25 bg-emerald-950/15 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-emerald-100">Render preview ready</p>
                    <p className="text-xs text-slate-400">
                      Open in the video editor to trim and export, or export an MP4 to S3 and download now.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={openingEditor || exportingVideo}
                      onClick={handleOpenInEditor}
                      data-testid="saa-open-in-editor"
                    >
                      {openingEditor ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Film className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {openingEditor ? 'Preparing editor…' : 'Open in editor'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={openingEditor || exportingVideo}
                      onClick={handleExportMp4}
                      data-testid="saa-export-mp4"
                    >
                      {exportingVideo ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {exportingVideo ? 'Exporting…' : 'Export & download MP4'}
                    </Button>
                    {exportedVideoUrl ? (
                      <Button type="button" size="sm" variant="ghost" asChild>
                        <a href={exportedVideoUrl} target="_blank" rel="noreferrer">
                          S3 file
                          <ExternalLink className="w-3.5 h-3.5 ml-1" />
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <ScriptAudioAlignPreview result={result} onResultUpdate={handleResultUpdate} />
            </div>
          </>
        ) : null}
      </div>

      <ScriptAudioAlignPipelineDialog
        open={pipelineOpen}
        onOpenChange={setPipelineOpen}
        result={result}
        loading={loading}
        error={error}
      />

      <ScriptAudioAlignHistoryPanel
        open={historyOpen}
        onToggle={handleHistoryToggle}
        activeStoryId={activeStoryId}
        activeJobId={activeJobId}
        onSelectJob={handleSelectHistoryJob}
        onNewProject={handleNewProject}
        refreshKey={historyRefreshKey}
        loadingJobId={loadingJobId}
      />
    </div>
  );
}
